import { Box, Typography } from "@mui/material";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont, portalShellSx } from "../partnerTheme";
import PartnerPortalHeader from "./PartnerPortalHeader";
import CampaignHero from "./CampaignHero";
import OfferingOverviewSection from "./OfferingOverviewSection";
import ExperienceJourneySection from "./ExperienceJourneySection";
import CampaignOverviewGrid from "./CampaignOverviewGrid";
import ActivationCards from "./ActivationCard";
import PlatformPerformanceTable from "./PlatformPerformanceTable";
import PerformanceKpis from "./MetricCard";
import PhotoGallery from "./PhotoGallery";
import ReportPdfDownload from "./ReportPdfDownload";
import CampaignClosingCTA from "./CampaignClosingCTA";

/**
 * Reusable sponsor results template.
 * All visible content comes from `campaign`. Accent via CSS variables.
 */
export default function SponsorResultsPage({
  campaign,
  embedded = false,
  heroCopy,
}: {
  campaign: SponsorCampaign;
  embedded?: boolean;
  heroCopy?: { headline: string; summary: string; points?: readonly string[] };
}) {
  const heroCampaign = heroCopy
    ? { ...campaign, headline: heroCopy.headline, headlineLine2: undefined, campaignSummary: heroCopy.summary }
    : campaign;

  return (
    <Box sx={{ ...portalShellSx(campaign), minHeight: embedded ? "auto" : "100vh" }}>
      {embedded ? null : <PartnerPortalHeader campaign={campaign} />}

      {campaign.sampleDisclaimer ? (
        <Box
          role="note"
          sx={{
            maxWidth: PORTAL_MAX_WIDTH,
            mx: "auto",
            px: { xs: 2, md: 4 },
            pt: { xs: 1.5, md: 2 },
            pb: 0,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: "12px",
              border: "1px solid color-mix(in srgb, var(--portal-accent) 40%, transparent)",
              bgcolor: "color-mix(in srgb, var(--portal-accent) 10%, transparent)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: { xs: "0.8125rem", md: "0.875rem" },
                color: "var(--portal-text)",
                lineHeight: 1.45,
              }}
            >
              {campaign.sampleDisclaimer}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <CampaignHero campaign={heroCampaign} points={heroCopy?.points} />

      {campaign.offeringOverview ? (
        <OfferingOverviewSection overview={campaign.offeringOverview} />
      ) : null}

      {campaign.experienceJourney ? (
        <ExperienceJourneySection journey={campaign.experienceJourney} />
      ) : null}

      <CampaignOverviewGrid
        fields={campaign.glanceFields}
        eyebrow={campaign.glanceSection ? campaign.glanceSection.eyebrow ?? "" : undefined}
        title={campaign.glanceSection ? campaign.glanceSection.title ?? "" : undefined}
      />
      <ActivationCards
        eyebrow={campaign.activationsSection.eyebrow}
        title={campaign.activationsSection.title}
        description={campaign.activationsSection.description}
        activations={campaign.activations}
      />
      <PlatformPerformanceTable data={campaign.platformPerformance} />
      <PerformanceKpis
        eyebrow={campaign.kpiSection.eyebrow}
        title={campaign.kpiSection.title}
        description={campaign.kpiSection.description}
        groups={campaign.kpiSection.groups}
      />
      <PhotoGallery
        eyebrow={campaign.gallery.eyebrow}
        title={campaign.gallery.title}
        description={campaign.gallery.description}
        images={campaign.gallery.images}
        pendingMessage={campaign.gallery.pendingMessage}
      />
      {campaign.assets.reportPdf ? (
        <ReportPdfDownload campaign={campaign} />
      ) : null}
      <CampaignClosingCTA closing={campaign.closing} />
    </Box>
  );
}
